import { useCallback, useEffect, useRef } from 'react';
import Debug from 'debug';

import { reportErrorClient } from '~/app/lib/sentry/client';
import { tryParseJson } from '~/app/lib/utils/object';
import { assertEnvVar } from '~/app/lib/utils/assert';
import openPopup from '~/app/lib/utils/openPopup';
import { useAppRouter } from '~/app/lib/router2';
import onceIdle from '~/app/lib/utils/onceIdle';

import {
  SpotifyAuthResult,
  SpotifyAuthResultSuccess,
} from '~/pages/api/spotify/auth/callback';

import { ServiceAuthProps } from '../types';

import { composeSpotifyAuthUrl, resolveSpotifyAppUrl } from './utils';
import { SPOTIFY_AUTH_PAYLOAD_PARAM } from './constants';

export * from './constants';

export const SPOTIFY_CLIENT_ID = assertEnvVar(
  process.env.SPOTIFY_CLIENT_ID,
  'SPOTIFY_CLIENT_ID'
);

const debug = Debug('songwhip/useSpotifyAuth');

export type SpotifyAuthProps = ServiceAuthProps<SpotifyAuthResultSuccess>;

export const useSpotifyAuth = ({
  albumId,

  onSuccess,
  onError,
}: SpotifyAuthProps) => {
  const childWinRef = useRef<Window | null>(null);
  const appRouter = useAppRouter();

  const fallbackAuthPayloadParam =
    appRouter.getAsQuery()[SPOTIFY_AUTH_PAYLOAD_PARAM];

  const handleAuthPayload = (payload: SpotifyAuthResult) => {
    debug('handle auth payload', payload);

    switch (payload.type) {
      case 'SPOTIFY_AUTH_SUCCESS':
        onSuccess(payload);

        break;

      case 'SPOTIFY_AUTH_ERROR':
        // https://developer.spotify.com/documentation/web-api/tutorials/code-flow
        if (payload.error !== 'access_denied') {
          const message = 'Spotify Auth error';

          reportErrorClient({
            error: new Error(message),
            extras: { error: payload.error },
          });

          onError?.(message);
        }

        break;
    }
  };

  useEffect(() => {
    // the child popup window will postMessage this window when
    // the auth flow is complete, passing the google token payload
    const onMessage = ({ data }) => {
      debug('postMessage', data);

      const payload = tryParseJson<SpotifyAuthResultSuccess>(data);

      const shouldHandle =
        !!payload?.type && !!~payload.type.indexOf('SPOTIFY');

      // abort if payload isn't as expected
      if (!shouldHandle) return;

      // close that popup asap
      childWinRef.current?.close();

      handleAuthPayload(payload);
    };

    addEventListener('message', onMessage);

    // detach global event listener on unmount
    return () => {
      removeEventListener('message', onMessage);
    };
  }, [albumId]);

  /**
   * When oauth popup flow fails to postMessage back to the parent window
   * it will redirect back to the `fallbackPath` passing the auth payload
   * as a query param. This can happen when app is running inside a webview.
   */
  useEffect(() => {
    if (!fallbackAuthPayloadParam) return;

    const authPayload = tryParseJson<SpotifyAuthResultSuccess>(
      fallbackAuthPayloadParam
    );

    if (!authPayload) {
      return;
    }

    // Remove auth result param from URL
    const parsedUrl = new URL(location.href);
    parsedUrl.searchParams.delete(SPOTIFY_AUTH_PAYLOAD_PARAM);
    history.replaceState(history.state, '', parsedUrl.toString());

    handleAuthPayload(authPayload);
  }, [fallbackAuthPayloadParam]);

  return useCallback(
    ({
      fallbackUrl,
    }: {
      /**
       * Used to provide a fallbackUrl when postMessage fails
       * and we need to redirect the popup back to this page (usually for webviews).
       * It's also used to derive a postMessage `origin` used for security.
       * It's important to keep hash params to ensure any dialog UI state is
       * preserved if a page reload happens.
       */
      fallbackUrl: string;
    }) => {
      const url = new URL('https://accounts.spotify.com/authorize');
      const deepLink = resolveSpotifyAppUrl(navigator.userAgent);

      const composedUrl = composeSpotifyAuthUrl({
        clientId: SPOTIFY_CLIENT_ID,
        url,
        fallbackUrl,
      });

      if (deepLink) {
        const composedDeepLink = composeSpotifyAuthUrl({
          clientId: SPOTIFY_CLIENT_ID,
          url: deepLink,
          fallbackUrl,
        });

        openPopup({
          url: composedDeepLink.toString(),
          target: '_self',
        });

        // as we cannot detect if the app is installed or not
        // we open with idle (to make sure we tried to open the app)
        // the http auth as a fallback every time
        onceIdle(() => {
          location.assign(composedUrl.toString());
        });
      } else {
        childWinRef.current = openPopup({
          url: composedUrl.toString(),
          width: 480,
          height: 640,
        });
      }
    },
    []
  );
};
